import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import type { TeamMember, TeamMemberCategory, SocialLink } from '../../types';
import { storage, db } from '../../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, getDoc, addDoc, updateDoc, collection, getDocs } from 'firebase/firestore';
import { useToast } from '../../hooks/useToast';
import ImageCropUploader from '../../components/ImageCropUploader';
import { GoogleGenAI, Type } from '@google/genai';

const TEAM_MEMBER_CATEGORIES: TeamMemberCategory[] = ['Architects', 'Professionals', 'Semi-Professionals', 'Technicals', 'Administrative'];
const SparklesIcon: React.FC<{className?:string}> = ({className}) => <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m12 3-1.9 5.8-5.8 1.9 5.8 1.9 1.9 5.8 1.9-5.8 5.8-1.9-5.8-1.9z"/></svg>;


const AdminTeamMemberFormPage: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const isEditing = Boolean(id);
    const { showToast } = useToast();
    const [isLoading, setIsLoading] = useState(isEditing);
    const [isSaving, setIsSaving] = useState(false);
    const [imageBlob, setImageBlob] = useState<Blob | null>(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isGeneratingExpertise, setIsGeneratingExpertise] = useState(false);
    const [expertiseKeywords, setExpertiseKeywords] = useState('');

    const [member, setMember] = useState<Omit<TeamMember, 'id'>>({
        name: '',
        role: '',
        imageUrl: '',
        professionalAffiliation: '',
        shortDescription: '',
        expertise: [],
        category: 'Architects',
        order: 0,
        socialLinks: [],
    });

    useEffect(() => {
        const fetchInitialData = async () => {
            if (isEditing && id) {
                const docRef = doc(db, 'teamMembers', id);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    setMember(docSnap.data() as TeamMember);
                } else {
                    showToast('Team member not found.', { type: 'error' });
                    navigate('/admin/team');
                }
            } else {
                // Get order for new member
                const snapshot = await getDocs(collection(db, 'teamMembers'));
                setMember(prev => ({ ...prev, order: snapshot.size }));
            }
            setIsLoading(false);
        }
        fetchInitialData();
    }, [id, isEditing, navigate, showToast]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setMember(prev => ({ ...prev, [name]: value }));
    };

    const handleSocialLinkChange = (index: number, field: keyof SocialLink, value: string) => {
        const newLinks = [...(member.socialLinks || [])];
        newLinks[index] = { ...newLinks[index], [field]: value };
        setMember(prev => ({ ...prev, socialLinks: newLinks }));
    };

    const addSocialLink = () => {
        const newLinks = [...(member.socialLinks || []), { platform: '', url: '' }];
        setMember(prev => ({ ...prev, socialLinks: newLinks }));
    };

    const removeSocialLink = (index: number) => {
        const newLinks = (member.socialLinks || []).filter((_, i) => i !== index);
        setMember(prev => ({ ...prev, socialLinks: newLinks }));
    };
    
    const handleImageUpload = async (): Promise<string> => {
        if (!imageBlob) return member.imageUrl;

        try {
            const storageRef = ref(storage, `team/${Date.now()}_avatar.png`);
            await uploadBytes(storageRef, imageBlob, { contentType: 'image/png' });
            return await getDownloadURL(storageRef);
        } catch (error) {
            console.error("Error uploading image:", error);
            showToast('Failed to upload cropped image.', { type: 'error' });
            throw error; // Propagate error to stop form submission
        }
    };

    const handleGenerateDescription = async () => {
        if (!member.shortDescription?.trim()) {
            showToast("Please enter some keywords or a draft to improve.", { type: 'error' });
            return;
        }
        setIsGenerating(true);
        try {
            const ai = new GoogleGenAI({ apiKey: process.env.API_KEY as string });
            const firstName = member.name.split(' ')[0] || 'This team member';
            const prompt = `As a professional corporate copywriter, write a professional bio of approximately 50-60 words for a team member named "${member.name}" with the role of "${member.role}". Use the following notes/keywords as a basis: "${member.shortDescription}". The tone should be polished and impactful. Instead of using pronouns like 'He' or 'She', please refer to the person by their first name (e.g., '${firstName} is an architect...' instead of 'He is an architect...'). Return only the bio text, without any markdown formatting.`;
            
            const response = await ai.models.generateContent({
                // FIX: Update model to recommended model for text generation.
                model: 'gemini-3-flash-preview',
                contents: prompt,
            });
    
            const text = response.text.trim();
            if (text) {
                setMember(prev => ({ ...prev, shortDescription: text }));
                showToast('Short description generated!', { type: 'success' });
            } else {
                throw new Error("AI did not return any text.");
            }
        } catch (error) {
            console.error("AI description generation failed:", error);
            showToast("Sorry, there was an error generating the description.", { type: 'error' });
        } finally {
            setIsGenerating(false);
        }
    };

    const handleGenerateExpertise = async () => {
        const promptText = expertiseKeywords.trim();
        if (!promptText) {
            showToast("Please provide some keywords about the member's skills.", { type: 'error' });
            return;
        }
        setIsGeneratingExpertise(true);
        try {
            const ai = new GoogleGenAI({ apiKey: process.env.API_KEY as string });
            const prompt = `Based on a team member with the role "${member.role}" and the following keywords: "${promptText}", generate a JSON array of 3-5 key expertise areas. For example, if the role is "Lead Architect" and keywords are "sustainable housing, urban planning", a good response would be ["Sustainable Architecture", "Urban Masterplanning", "Residential Design", "Green Building Materials"]. Return ONLY the JSON array.`;
            
            const response = await ai.models.generateContent({
                // FIX: Update model to recommended model for JSON generation.
                model: 'gemini-3-flash-preview',
                contents: prompt,
                config: {
                    responseMimeType: "application/json",
                    responseSchema: {
                        type: Type.ARRAY,
                        items: { type: Type.STRING }
                    }
                }
            });
    
            const jsonStr = response.text.trim();
            const expertiseArray = JSON.parse(jsonStr);
    
            if (Array.isArray(expertiseArray) && expertiseArray.every(item => typeof item === 'string')) {
                setMember(prev => ({ ...prev, expertise: expertiseArray }));
                showToast('Expertise list generated!', { type: 'success' });
            } else {
                throw new Error("AI did not return a valid JSON array of strings.");
            }
        } catch (error) {
            console.error("AI expertise generation failed:", error);
            showToast("Sorry, there was an error generating the expertise list.", { type: 'error' });
        } finally {
            setIsGeneratingExpertise(false);
        }
    };

    const removeExpertise = (index: number) => {
        const newExpertise = (member.expertise || []).filter((_, i) => i !== index);
        setMember(prev => ({ ...prev, expertise: newExpertise }));
    };


    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            let finalImageUrl = member.imageUrl;
            if (imageBlob) {
                finalImageUrl = await handleImageUpload();
            }

            const memberData = { ...member, imageUrl: finalImageUrl };

            if (isEditing && id) {
                const memberRef = doc(db, 'teamMembers', id);
                await updateDoc(memberRef, memberData);
                showToast('Team member updated successfully!', { type: 'success' });
            } else {
                await addDoc(collection(db, 'teamMembers'), memberData);
                showToast('Team member added successfully!', { type: 'success' });
            }
            navigate('/admin/team');
        } catch (error) {
            console.error("Error saving team member:", error);
            let message = 'Failed to save team member. Please check the console.';
            if (String(error).includes('storage/unauthorized')) {
                message = "Save failed due to storage permissions. Please update your Firebase Storage rules to allow write access for authenticated users.";
            }
            showToast(message, { type: 'error' });
            setIsSaving(false);
        }
    };
    
    if (isLoading) {
        return <div className="text-center p-8">Loading team member details...</div>;
    }

    return (
        <div>
            <h2 className="text-3xl font-bold text-gray-800 mb-6">{isEditing ? 'Edit Team Member' : 'Add New Team Member'}</h2>
            <form onSubmit={handleSubmit} className="space-y-8">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    <div className="lg:col-span-2 space-y-6 bg-white p-8 rounded-lg shadow-md h-fit">
                        <div>
                            <label htmlFor="name" className="block text-sm font-medium text-gray-700">Full Name</label>
                            <input type="text" name="name" id="name" value={member.name} onChange={handleChange} required className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500"/>
                        </div>
                        <div>
                            <label htmlFor="role" className="block text-sm font-medium text-gray-700">Designation</label>
                            <input type="text" name="role" id="role" value={member.role} onChange={handleChange} required className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500"/>
                        </div>
                         <div>
                            <label htmlFor="professionalAffiliation" className="block text-sm font-medium text-gray-700">Professional Affiliation</label>
                            <input type="text" name="professionalAffiliation" id="professionalAffiliation" value={member.professionalAffiliation} onChange={handleChange} placeholder="e.g., APAM, RIBA" className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500"/>
                        </div>
                        <div>
                            <label htmlFor="category" className="block text-sm font-medium text-gray-700">Category</label>
                            <select name="category" id="category" value={member.category} onChange={handleChange} required className="mt-1 block w-full px-3 py-2 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500">
                                {TEAM_MEMBER_CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                            </select>
                        </div>
                        <div>
                            <label htmlFor="shortDescription" className="block text-sm font-medium text-gray-700">Bio / Short Description</label>
                            <div className="relative">
                                <textarea name="shortDescription" id="shortDescription" rows={4} value={member.shortDescription || ''} onChange={handleChange} placeholder="e.g., 15+ years of experience in sustainable residential design..." className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500 pr-10"></textarea>
                                <button
                                    type="button"
                                    onClick={handleGenerateDescription}
                                    disabled={isGenerating}
                                    className="absolute top-2.5 right-1.5 p-1 text-gray-400 hover:text-indigo-600 disabled:opacity-50 transition-colors"
                                    title="Improve with AI"
                                >
                                    <SparklesIcon className={`h-5 w-5 ${isGenerating ? 'animate-pulse text-indigo-600' : ''}`} />
                                </button>
                            </div>
                        </div>

                        <div className="border-t pt-6">
                            <h3 className="text-lg font-semibold text-gray-800 mb-2">Key Expertise</h3>
                            {member.expertise && member.expertise.length > 0 && (
                                <div className="flex flex-wrap gap-2 mb-4">
                                    {member.expertise.map((skill, index) => (
                                        <div key={index} className="bg-gray-200 text-gray-800 text-sm font-medium pl-3 pr-2 py-1 rounded-full flex items-center gap-2">
                                            <span>{skill}</span>
                                            <button type="button" onClick={() => removeExpertise(index)} className="w-4 h-4 rounded-full bg-gray-400 text-white flex items-center justify-center text-xs hover:bg-red-500">&times;</button>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <div>
                                <label htmlFor="expertiseKeywords" className="block text-sm font-medium text-gray-700">Generate with AI</label>
                                <div className="flex items-center gap-2 mt-1">
                                    <input 
                                        type="text" 
                                        id="expertiseKeywords" 
                                        value={expertiseKeywords} 
                                        onChange={e => setExpertiseKeywords(e.target.value)} 
                                        placeholder="e.g., sustainable design, BIM, project management" 
                                        className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md"
                                    />
                                    <button 
                                        type="button" 
                                        onClick={handleGenerateExpertise} 
                                        disabled={isGeneratingExpertise} 
                                        className="px-3 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-600 disabled:bg-gray-400 flex items-center whitespace-nowrap text-sm"
                                    >
                                        <SparklesIcon className={`h-4 w-4 mr-1 ${isGeneratingExpertise ? 'animate-pulse' : ''}`} />
                                        {isGeneratingExpertise ? '...' : 'Generate'}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="border-t pt-6">
                            <h3 className="text-lg font-semibold text-gray-800 mb-2">Social Media Links</h3>
                             <div className="space-y-4">
                                {(member.socialLinks || []).map((link, index) => (
                                    <div key={index} className="flex items-center gap-4">
                                       <input type="text" value={link.platform} onChange={(e) => handleSocialLinkChange(index, 'platform', e.target.value)} placeholder="Platform (e.g., LinkedIn)" className="w-1/3 px-3 py-2 border border-gray-300 rounded-md"/>
                                       <input type="url" value={link.url} onChange={(e) => handleSocialLinkChange(index, 'url', e.target.value)} placeholder="Full URL (e.g., https://...)" className="flex-1 px-3 py-2 border border-gray-300 rounded-md"/>
                                       <button 
                                            type="button" 
                                            onClick={() => removeSocialLink(index)} 
                                            className="flex-shrink-0 w-8 h-8 bg-gray-200 text-gray-600 rounded-full flex items-center justify-center hover:bg-red-500 hover:text-white transition-colors text-sm font-bold"
                                            aria-label="Remove social link"
                                        >
                                            X
                                       </button>
                                    </div>
                                ))}
                            </div>
                            <button type="button" onClick={addSocialLink} className="mt-4 text-sm font-semibold text-gray-800 hover:text-black inline-flex items-center gap-2">
                                <span className="font-bold text-lg leading-none">+</span> Add Social Link
                            </button>
                        </div>
                    </div>
                    <div className="space-y-6 bg-white p-8 rounded-lg shadow-md h-fit">
                         <ImageCropUploader 
                            onSave={setImageBlob}
                            initialImageUrl={member.imageUrl}
                            aspect={1}
                        />
                    </div>
                </div>

                <div className="flex justify-end space-x-4 bg-white p-4 rounded-lg shadow-md sticky bottom-4">
                    <button type="button" onClick={() => navigate('/admin/team')} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300">
                        Cancel
                    </button>
                    <button type="submit" disabled={isSaving} className="px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-700 disabled:bg-gray-400">
                        {isSaving ? 'Saving...' : 'Save Member'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AdminTeamMemberFormPage;